import { queryOptions } from "@tanstack/react-query";

import type { UnifiChangeBody, UnifiOverview, UnifiProjectNetwork } from "@/lib/unifi/types";

async function readJson<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    // SAFETY: our admin routes answer errors with the `{ message }` envelope.
    const body = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message ?? fallback);
  }

  // SAFETY: success bodies of our own UniFi routes match the documented types.
  return (await res.json()) as T;
}

/** Everything /admin/integrations/unifi shows, as last synced. */
export const unifiOverviewQueryOptions = queryOptions({
  queryKey: ["admin", "unifi", "overview"],
  queryFn: async () =>
    readJson<UnifiOverview>(
      await fetch("/api/admin/unifi", { cache: "no-store" }),
      "Impossible de charger les données UniFi.",
    ),
  // A running sync updates rows every few seconds; poll until it ends.
  refetchInterval: (query) => (query.state.data?.running ? 3_000 : false),
});

/** The consoles and UniFi sites of one project. */
export function unifiProjectNetworkQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: ["admin", "unifi", "project", projectId],
    queryFn: async () =>
      readJson<UnifiProjectNetwork>(
        await fetch(`/api/admin/unifi/projects/${encodeURIComponent(projectId)}`, {
          cache: "no-store",
        }),
        "Impossible de lire le réseau du projet.",
      ),
  });
}

/** PATCH/POST helper for the UniFi admin routes, throwing their French message. */
export async function sendUnifi<T>(path: string, method: "PATCH" | "POST", body?: UnifiChangeBody) {
  return readJson<T>(
    await fetch(path, {
      method,
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
    "La modification n'a pas été enregistrée.",
  );
}
